
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp } from 'lucide-react';
import MarkdownText from './MarkdownText';
import { getCategoryIcon, getCategoryColor, getCategoryLabel, truncateText } from './sectionCardUtils';

interface SectionCardProps {
  section: {
    title: string;
    content: string;
    category: string;
  };
  isExpanded: boolean;
  onToggle: () => void;
  index: number;
}

export default function SectionCard({ section, isExpanded, onToggle, index }: SectionCardProps) {
  const IconComponent = getCategoryIcon(section.category);
  const colors = getCategoryColor(section.category);
  const shouldShowToggle = section.content && section.content.length > 300;

  // Clean and validate content
  const cleanContent = section.content || 'Conteúdo não disponível para esta seção.';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="group"
    >
      <Card className={`${colors.border} ${colors.bg} border-2 transition-all duration-300 hover:shadow-lg overflow-hidden relative`}>
        {/* Gradient header accent */}
        <div className={`h-2 bg-gradient-to-r ${colors.gradient}`} />
        
        <CardHeader className="pb-3 relative">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className={`p-2 rounded-lg bg-gradient-to-r ${colors.gradient} text-white shadow-sm flex-shrink-0`}>
                <IconComponent className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <CardTitle className="text-base font-bold text-gray-800 leading-tight break-words">
                  {section.title}
                </CardTitle>
                <Badge 
                  variant="outline" 
                  className={`text-xs font-semibold ${colors.badge} border mt-1 flex-shrink-0`}
                >
                  {getCategoryLabel(section.category)}
                </Badge>
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0">
          <div className="space-y-3">
            <div className="text-sm leading-relaxed text-justify hyphens-auto break-words text-gray-700">
              <MarkdownText 
                text={isExpanded ? cleanContent : truncateText(cleanContent, 300)} 
              />
            </div>
            
            {/* Toggle button */}
            {shouldShowToggle && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggle}
                className="self-start text-gray-600 hover:text-gray-800 p-1 h-auto"
              >
                <span className="text-xs mr-1">
                  {isExpanded ? 'Ver menos' : 'Ver mais'}
                </span>
                {isExpanded ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
              </Button>
            )}
          </div>
        </CardContent>
        
        {/* Subtle decorative element */}
        <div className={`absolute top-4 right-4 w-6 h-6 bg-gradient-to-r ${colors.gradient} rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-300`} />
      </Card>
    </motion.div>
  );
}
